import Image from '@/components/Image';
import cn from 'classnames';
import { FC } from 'react';

type Logo = {
  id: string | number;
  customerName: string;
  svgUrl: string;
  height: number;
  width: number;
  isTaller: boolean;
}

type Award = {
  id: string | number;
  svgUrl?: string;
  width: number;
  height: number;
  sizeMultiplier?: number;
}

export type LogoFarmProps = {
  logosHeading?: string;
  logos: Logo[];
  awardsHeading?: string;
  awards: Award[];
}

export const LogoFarm: FC<LogoFarmProps> = ({ awards, logos, awardsHeading, logosHeading }) => {
  return (
    <div className='px-4 md:px-8'>
      {logosHeading ? (
        <h3
          id="logo-farm-heading"
          className="text-16 leading-21 text-center font-700 mb-5 sm:mb-4 xl:text-24 xl:leading-30 xl:font-600 2xl:text-32 2xl:leading-38 2xl:mb-8"
        >
          {logosHeading}
        </h3>
      ) : null}

      <div className="flex items-end justify-center flex-row flex-wrap gap-y-3 gap-x-5">
        {logos.map((logo) => (
          <div key={logo.id}>
            <Image
              className={cn('w-auto', {
                // logo-size-modifier CSS variable allows dynamic scaling of logo heights (defaults to 1 if not set)
                'h-[calc(52px_*_var(--logo-size-modifier,1))] 2xl:h-[calc(58px_*_var(--logo-size-modifier,1))]':
                  logo.isTaller,
                'h-[calc(39px_*_var(--logo-size-modifier,1))] 2xl:h-[calc(43px_*_var(--logo-size-modifier,1))]':
                  !logo.isTaller,
              })}
              src={logo.svgUrl}
              width={logo.width}
              height={logo.height}
              alt={`${logo.customerName} logo`}
            />
          </div>
        ))}
      </div>

      {awards.length > 0 && (
        <>
          {awardsHeading ? (
            <h3
              id="logo-farm-awards-heading"
              className="text-16 leading-21 text-center font-700 mt-5 mb-3 sm:mt-6 sm:mb-4 xl:text-24 xl:leading-34
               xl:font-600  xl:mb-5 2xl:mt-8"
            >
              {awardsHeading}
            </h3>
          ) : null}

          <div
            className="grid grid-cols-3 sm:grid-cols-none sm:flex sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-x-2
           lg:gap-x-3 2xl:gap-x-6 [&>*:nth-child(3n-2)]:mt-4 [&>*:nth-child(3n-1)]:mt-2 sm:[&>*:nth-child(3n-2)]:mt-0
            sm:[&>*:nth-child(3n-1)]:mt-0 sm:[&>*:nth-child(3n)]:mt-0"
          // nth-child selectors create diagonal display on mobile: 1st item gets mt-4, 2nd gets mt-2, 3rd gets no margin
          >
            {awards.map((award) => (
              <div
                key={award.id}
                className="mx-auto sm:mx-0"
                style={{
                  // Allows the award height to be dynamically scaled based on the size multiplier
                  '--award-height': `${String(award.height * (award.sizeMultiplier || 1))}px`
                } as React.CSSProperties}              >

                <Image
                  className="w-auto h-[var(--award-height)]"
                  src={award.svgUrl!}
                  width={award.width}
                  height={award.height}
                  alt="Award badge"
                />

              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
